import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { SERIAL_SPACING } from "src/components/Planner/planner/serial-layout";
import {
  getModRoomIfId,
  getOuterRooms,
  getRoomById,
  isCamera,
  isRoomOutside,
  uDev,
  UDevGlobals
} from "src/components/Planner/planner/uDev";
import {wallModHelper} from "src/components/Planner/planner/wallModHelper";
import * as d3 from "d3";

/**
 * Contains Math functions for uplan
 */
export let uMath = {
  /**
   *
   * @param {Segment} segment
   * @param {number} lengthPercentage
   */
  shortenSegment: function (segment, minusPercent) {
    const pt1 = segPt(segment, 1);
    const pt2 = segPt(segment, 2);
    const shortPt1 = this.lerpPt(pt1, pt2, minusPercent);
    const shortPt2 = this.lerpPt(pt1, pt2, 1 - minusPercent);
    return uMath.segmentFromPoints(shortPt1, shortPt2);
  },
  /**
   *
   * @param {Point[]} poly
   */
  getStraightPathsOfPoly: function (poly, maxAllowedAngle = 8) {
    var paths = [];
    for (var i = 0; i < poly.length; i++) {
      var curr = poly[i];
      var next = poly[(i + 1) % poly.length];
      paths.push({
        points: [curr, next],
        path: uMath.segmentFromPoints(curr, next),
        angle: vectorRotationDegree(curr, next),
      });
    }

    var currAngleIdx = 0;
    var i = 1;
    while (i < paths.length) {
      if (wallModHelper.angleSimilar(paths[i - 1].angle, paths[i].angle, maxAllowedAngle)) {
        paths[currAngleIdx].points.push(paths[i].points[1]);
        paths[i].toDelete = true;
      }
      else {
        currAngleIdx = i;
      }
      i++;
    }

    var i = paths.length - 1;
    while (wallModHelper.angleSimilar(paths[0].angle, paths[i].angle, maxAllowedAngle)) {
      paths[0].points.unshift(paths[i].points[0]);
      paths[i].toDelete = true;
      i--;
    }

    paths = paths.filter(function (path) {
      return !("toDelete" in path);
    });

    return paths;
  },
  /**
   *
   * @param {Segment} s0
   * @param {Segment} s1
   */
  segmentToSegmentProjection: function (s0, s1) {
    var p1 = segmentClosestPointToPoint_S(segPt(s0, 1), s1);
    var p2 = segmentClosestPointToPoint_S(segPt(s0, 2), s1);
    return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
  },
  /**
   *
   * @param {WallMod} wallMod
   * @returns {Segment}
   */
  wallModOuterWallClosestSegment: function (wallMod) {
    if (uDev.outerBorderExists()) {
      var closestResult = uMath.pointClosestPtToPolygon(uMath.segmentCenter(wallModHelper.segmentFromWallMod(wallMod)), PlannerGlobals.outerBorder);
      if (closestResult && closestResult.segment) {
        return closestResult.segment;
      }
      else {
        return null;
      }
    }
    else {
      return null;
    }
  },
  /**
   *
   * @param {WallMod} wallMod
   * @returns {Segment}
   */
  wallModOuterWallClosestStraightPath: function (wallMod) {
    if (uDev.outerBorderExists()) {
      var allWalls = PlannerGlobals.rooms.map(function (room) { return wallsOfRoom(room); }).reduce(function (p, c) { return p.concat(c) }, []);
      var wallModSegment = wallModHelper.segmentFromWallMod(wallMod);
      var wallModCenter = uMath.segmentCenter(wallModSegment);
      var simplifiedOuterBorderStraightPaths = uMath.getStraightPathsOfPoly(PlannerGlobals.outerBorder).map(function (path) {
        return uMath.segmentFromPoints(path.points[0], path.points[path.points.length - 1]);
      });
      var minIntersects = -1;
      var minDist = -1;
      var minSegment = -1;
      simplifiedOuterBorderStraightPaths.forEach(function (borderSegment) {
        var closestPt = segmentClosestPointToPoint_S(wallModCenter, borderSegment);
        var dist = uMath.pointDistance(wallModCenter, closestPt);
        var intersects = allWalls.filter(function (wall) { return line_line_intersect2(wall, uMath.segmentFromPoints(wallModCenter, closestPt)) }).length;
        if (minSegment === -1 || intersects < minIntersects || intersects === minIntersects && dist < minDist) {
          minIntersects = intersects;
          minDist = dist;
          minSegment = borderSegment;
        }
      });
      return minSegment;
    }
    else {
      return null;
    }
  },
  /**
   *
   * @param {WallMod} wallMod
   */
  getOuterDoorWallProjection: function (wallMod) {
    var closestSegmentOnBorder = uMath.wallModOuterWallClosestStraightPath(wallMod);
    if (closestSegmentOnBorder) {
      var wallModSegment = wallModHelper.segmentFromWallMod(wallMod);
      return uMath.segmentToSegmentProjection(wallModSegment, closestSegmentOnBorder);
    }
    else {
      return null;
    }
  },
  /**
   * Linear Interpolation between vectors
   * @param {Point} v0
   * @param {Point} v1
   * @param {number} t
   */
  lerpPt: function (v0, v1, t) {
    var x0 = v0.x;
    var y0 = v0.y;
    var x1 = v1.x;
    var y1 = v1.y;
    return { x: this.lerp(x0, x1, t), y: this.lerp(y0, y1, t) };
  },
  /**
   * Linear Interpolation between numbers
   * @param {number} v0
   * @param {number} v1
   * @param {number} t
   */
  lerp: function (v0, v1, t) {
    return (1 - t) * v0 + t * v1;
  },
  /**
   *
   * @param {any[]} arr
   */
  _does: function (arr) {
    return {
      /**
       *
       * @param {any} item
       */
      contain: function (item) {
        return arr.indexOf(item) != -1;
      }
    }
  },
  /**
   *
   * @param {number} angle
   * @returns {number}
   */
  normalizeAngle: function (angle) {
    var nAngle = (angle % 360) || 0;
    if (nAngle < 0) {
      nAngle = 360 + nAngle;
    }
    return nAngle;
  },
  /**
   *
   * @param {Point} p1
   * @param {Point} p2
   * @returns {number}
   */
  pointDistance: function (p1, p2) {
    return Math.sqrt(Math.pow((p2.x - p1.x), 2) + Math.pow((p2.y - p1.y), 2))
  },
  /**
   *
   * @param {Line} l
   * @returns {number}
   */
  lineLength: function (l) {
    return this.pointDistance({ x: l.x1, y: l.y1 }, { x: l.x2, y: l.y2 });
  },
  /**
   *
   * @param {Point[]} polyline
   * @returns {number}
   */
  polyLineLength: function (polyline) {
    var length = 0;
    for (var i = 1; i < polyline.length; i++) {
      length += uMath.pointDistance(polyline[i - 1], polyline[i]);
    }
    return length;
  },
  /**
   *
   * @param {Segment} s
   * @returns {number}
   */
  segmentLength: function (s) {
    return this.pointDistance({ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 });
  },
  /**
   *
   * @param {Room} room
   */
  isRoomClockwise: function (room) {
    var walls = wallsOfRoom(room);
    var sum = 0;
    for (var i = 0; i < walls.length; i++) {
      var { x1, x2, y1, y2 } = walls[i];
      sum += (x2 - x1) * (y2 + y1);
    }
    return sum < 0;
  },
  /**
   *
   * @param {Point[]} polygon
   */
  isPolygonClockwise(polygon) {
    var segments = uMath.segmentsOfPolygon(polygon);
    var sum = 0;
    for (var i = 0; i < segments.length; i++) {
      var { x1, x2, y1, y2 } = segments[i];
      sum += (x2 - x1) * (y2 + y1);
    }
    return sum < 0;
  },
  /**
   *
   * @param {Room} room
   * @param {Point} point
   * @returns {boolean}
   */
  roomContainsPoint: function (room, point) {
    return d3.polygonContains(XYtoNumPoly(room.poly), [point.x, point.y]);
  },
  /**
   *
   * @param {Room} room
   * @returns
   */
  getRoomBoundingBox: function (room) {
    const toReturn = { left: null, top: null, right: null, bottom: null, width: null, height: null, centerX: null, centerY: null };
    if (!room) { return toReturn; }

    try {
      room.poly.forEach(elem => {
        if (toReturn.left === null || elem.x < toReturn.left) {
          toReturn.left = elem.x;
        }
        if (toReturn.top === null || elem.y < toReturn.top) {
          toReturn.top = elem.y;
        }
        if (toReturn.right === null || elem.x > toReturn.right) {
          toReturn.right = elem.x;
        }
        if (toReturn.bottom === null || elem.y > toReturn.bottom) {
          toReturn.bottom = elem.y;
        }
      });
      toReturn.width = toReturn.right - toReturn.left;
      toReturn.height = toReturn.bottom - toReturn.top;
      toReturn.centerX = toReturn.left + toReturn.width / 2;
      toReturn.centerY = toReturn.top + toReturn.height / 2;
    }
    catch (err) {
      console.debug(err);
    }

    return toReturn;
  },
  anyRoomContainsRectBot: function (rooms, bottomPt, width, height, angle) {
    var centerPt = this.pointAtAngleDistance(bottomPt, angle - 90, height / 2);
    var points = [];
    for (let i = -1; i <= 1; i += 2) {
      for (let j = -1; j <= 1; j += 2) {
        var pt = { x: centerPt.x + i * width / 2, y: centerPt.y + j * height / 2 };
        pt = rotatePointAroundAnotherXY(pt, centerPt, angle);
        points.push(pt);
      }
    }
    const result = rooms.some(room => points.every(pt => uMath.roomContainsPoint(room, pt)));
    return { result, points };
  },
  /**
   *
   * @param {Room} room
   * @param {Point} centerPt
   * @param {number} size
   */
  roomContainsRectBot: function (room, bottomPt, width, height, angle) {
    var centerPt = this.pointAtAngleDistance(bottomPt, angle - 90, height / 2);
    var points = [];
    for (let i = -1; i <= 1; i += 2) {
      for (let j = -1; j <= 1; j += 2) {
        var pt = { x: centerPt.x + i * width / 2, y: centerPt.y + j * height / 2 };
        pt = rotatePointAroundAnotherXY(pt, centerPt, angle);
        points.push(pt);
      }
    }
    const result = points.every(pt => uMath.roomContainsPoint(room, pt));
    return { result, points };
  },
  /**
   *
   * @param {Point} pt
   * @param {Point[]} ptArray
   * @param {number} distance
   * @returns {boolean}
   */
  farFromOthersInArray: function (pt, ptArray, distance) {
    return ptArray.every(otherPt => uMath.pointDistance(pt, otherPt) > distance);
  },
  degToRad: function (angle) {
    return angle * Math.PI / 180;
  },
  radToDeg: function (rad) {
    return rad * 180 / Math.PI;
  },
  cosD: function (degrees) {
    return Math.cos(degToRad(degrees));
  },
  sinD: function (degrees) {
    return Math.sin(degToRad(degrees));
  },
  /**
   *
   * @param {number[][]} poly
   * @param {number[]} point
   * @returns {boolean}
   */
  polygonContains: function (poly, point) {
    return d3.polygonContains(poly, point);
  },
  /**
   *
   * @param {Point} v1
   * @param {Point} v2
   * @returns {number}
   */
  dotProduct: function (v1, v2) {
    return v1.x * v2.x + v1.y * v2.y;
  },
  /**
   *
   * @param {Point} v
   * @returns {number}
   */
  vectorLength: function (v) {
    return uMath.lineLength({ x1: 0, y1: 0, x2: v.x, y2: v.y });
  },
  /**
   *
   * @param {Point} v1
   * @param {Point} v2
   * @returns {number}
   */
  vectorAngle: function (v1, v2) {
    return uMath.radToDeg(Math.acos(uMath.clamp(uMath.dotProduct(v1, v2) / (uMath.vectorLength(v1) * uMath.vectorLength(v2)), -1, 1)));
  },
  /**
   *
   * @param {Line} segment
   * @returns {number}
   */
  lineRotationDegree: function (segment) {
    return vectorRotationDegree({ x: segment.x1, y: segment.y1 }, { x: segment.x2, y: segment.y2 });
  },
  /**
   *
   * @param {Point} point
   * @param {number} scale
   * @returns {Point}
   */
  normalize: function (point, scale) {
    var norm = Math.sqrt(point.x * point.x + point.y * point.y);
    if (norm != 0) {
      point.x = scale * point.x / norm;
      point.y = scale * point.y / norm;
    }
    return point;
  },
  /**
   *
   * @param {number} val
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  clamp: function (val, min, max) {
    if (val < min) {
      return min;
    }
    else if (val > max) {
      return max;
    }
    else {
      return val;
    }
  },
  /**
   *
   * @param {Line} s Line Segment
   */
  vectorFromSegment: function (s) {
    return { x: s.x2 - s.x1, y: s.y2 - s.y1 };
  },
  /**
   *
   * @param {Point} p1
   * @param {Point} p2
   * @returns {Line}
   */
  segmentFromPoints: function (p1, p2) {
    return { x1: p1.x, x2: p2.x, y1: p1.y, y2: p2.y };
  },
  /**
   * Doesn't contain closing line
   * @param {Point[]} polyLine
   * @returns {Segment[]}
   */
  polyLineToSegments: function (polyLine) {
    var segments = [];
    for (var i = 0; i < polyLine.length - 1; i++) {
      var curr = polyLine[i];
      var next = polyLine[(i + 1) % polyLine.length];
      segments.push(this.segmentFromPoints(curr, next));
    }
    return segments;
  },
  /**
   * Polygon to array, so it contains last line
   * @param {Point[]} array
   * @returns {Segment[]}
   */
  segmentsFromPointArray: function (array) {
    var segments = [];
    for (var i = 0; i < array.length; i++) {
      var curr = array[i];
      var next = array[(i + 1) % array.length];
      segments.push(this.segmentFromPoints(curr, next));
    }
    return segments;
  },
  /**
   * same as upper...
   * @param {Point[]} polygon
   * @returns {Segment[]}
   */
  segmentsOfPolygon: function (polygon) {
    var segments = [];
    for (var i = 0; i < polygon.length; i++) {
      var c = polygon[i];
      var n = polygon[(i + 1) % polygon.length];
      segments.push(uMath.segmentFromPoints(c, n));
    }
    return segments;
  },
  /**
   *
   * @param {WallMod} wallMod
   * @returns {Segment}
   */
  segmentFromWallMod: function (wallMod) {
    return { x1: wallMod.points[0].x, y1: wallMod.points[0].y, x2: wallMod.points[1].x, y2: wallMod.points[1].y };
  },
  /**
   *
   * @param {Segment} segment
   * @param {Point[]} polygon
   */
  segmentPolygonIntersection: function (segment, polygon) {
    var polySegments = this.segmentsFromPointArray(polygon);
    for (var i = 0; i < polySegments.length; i++) {
      var polySegment = polySegments[i];
      var intersect = line_line_intersect(segment, polySegment)
      if (intersect != 'nir') {
        return intersect;
      }
    }
    return false;
  },
  /**
   *
   * @param {Segment} segment
   * @param {Point[][]} polygons
   */
  segmentIntersectsAnyPolygon: function (segment, polygons) {
    for (var i = 0; i < polygons.length; i++) {
      var polygon = polygons[i];
      if (uMath.segmentPolygonIntersection(segment, polygon)) {
        return true;
      }
    }
    return false;
  },
  /**
   *
   * @param {Segment} segment
   * @param {Point[][]} polygons
   */
  segmentIntersectsWhichPolygons: function (segment, polygons) {
    var polygonsIntersected = [];
    for (var i = 0; i < polygons.length; i++) {
      var polygon = polygons[i];
      if (uMath.segmentPolygonIntersection(segment, polygon)) {
        polygonsIntersected.push(polygon);
      }
    }
    return polygonsIntersected;
  },
  /**
   *
   * @param {d3.Selection<any>} input
   * @returns {SVGRect}
   */
  getBBoxIfExists(input) {
    if (input && typeof input.node == "function") {
      var node = input.node();
      if (node && typeof node.getBBox == "function") {
        var bbox = node.getBBox();
        if (bbox) {
          return bbox;
        }
        else { return false; }
      }
      else { return false; }
    }
    else { return false; }
  },
  /**
   *
   * @param {Point} point
   * @param {Point[]} polygon
   * @returns {Point}
   */
  pointClosestPtToPolygon: function (point, polygon) {
    var polySegments = this.segmentsFromPointArray(polygon);
    var candidates = [];
    polySegments.forEach(function (segment) {
      var closestPt = segmentClosestPointToPoint_S(point, segment);
      var dist = uMath.pointDistance(point, closestPt);
      candidates.push({ dist, closestPt, segment });
    });
    candidates.sort(function (a, b) {
      return a.dist < b.dist ? -1 : 1;
    });
    var result = candidates[0].closestPt;
    result.segment = candidates[0].segment;
    return result;
  },
  pointPolygonDistance: function (point, polygon){
    const closestPt = uMath.pointClosestPtToPolygon(point, polygon);
    return uMath.pointDistance(point, closestPt);
  },
  /**
   *
   * @param {Point} origin Original point
   * @param {number} dx x translate
   * @param {number} dy y translate
   * @param {number} angle angle of translation
   */
  translatePoint: function (origin, dx, dy, angle) {
    return P_numtoxy(rotatePointAroundAnother([origin.x + dx, origin.y + dy], [origin.x, origin.y], angle));
  },
  /**
   *
   * @param {Line} segment
   */
  segmentCenter: function (segment) {
    return { x: (segment.x1 + segment.x2) / 2, y: (segment.y1 + segment.y2) / 2 };
  },
  /**
   *
   * @param {Segment} segment
   * @param {number} percent 0% is 0, 100% is 1, etc
   */
  segmentPointAtPercent: function (segment, percent) {
    var diff = { x: segment.x2 - segment.x1, y: segment.y2 - segment.y1 };
    return { x: segment.x1 + percent * diff.x, y: segment.y1 + percent * diff.y };
  },
  /**
   *
   * @param {Point} p1
   * @param {Point} p2
   * @returns {Point}
   */
  pointsCenter: function (p1, p2) {
    return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  },
  /**
   *
   * @param {Rect} rect
   * @param {Point} point
   */
  rectContainsPoint: function (rect, point) {
    return point.x >= rect.x &&
        point.x <= rect.x + rect.width &&
        point.y >= rect.y &&
        point.y <= rect.y + rect.height;
  },
  /**
   *
   * @param {Rect} rect
   * @returns {Line[]}
   */
  linesOfRect: function (rect) {
    var points = [];
    for (var i = 0; i < 2; i++) {
      for (var j = 0; j < 2; j++) {
        points.push({ x: rect.x + i * rect.width, y: rect.y + j * rect.height });
      }
    }
    var lines = [
      uMath.segmentFromPoints(points[0], points[1]),
      uMath.segmentFromPoints(points[0], points[2]),
      uMath.segmentFromPoints(points[1], points[2]),
      uMath.segmentFromPoints(points[2], points[3]),
    ];
    return lines;
  },
  /**
   *
   * @param {Rect} rect
   * @param {Line} segment
   */
  rectIntersectsSegment: function (rect, segment) {
    return uMath.linesOfRect(rect)
    .filter(function (line) {
      return line_line_intersect(line, segment) != "nir";
    })
        .length != 0;
  },
  vectorRotationDegree: vectorRotationDegree,
  angleOfPoints: function (p1, p2) {
    return vectorRotationDegree(p1, p2);
  },
  /**
   *
   * @param {Point} point
   * @param {number} angle
   * @param {number} distance
   */
  pointAtAngleDistance: function (point, angle, distance) {
    return {
      x: point.x + uMath.cosD(angle) * distance,
      y: point.y + uMath.sinD(angle) * distance
    }
  },
  /**
   *
   * @param {Point[]} p1
   * @param {Point[]} p2
   */
  polygonsIntersect: function (p1, p2) {
    var segments1 = uMath.segmentsFromPointArray(p1);
    for (var seg1 of segments1) {
      if (uMath.segmentPolygonIntersection(seg1, p2)) {
        return true;
      }
    }
    return false;
  },
  /**
   *
   * @param {Point[]} p1
   * @param {Point[]} p2
   */
  polygonsInLine: function (p1, p2) {
    return p1.some(polyPt => {
      const p2Segments = uMath.segmentsFromPointArray(p2);
      return p2Segments.some(p2Segment => uMath.pointSegmentDistance_S(polyPt, p2Segment) < 0.001);
    });
  },
  polygonClipping: {
    /**
     *
     * @param {[number,number][]} p1
     * @param {[number,number][]} p2
     * @returns {[number, number][]}
     */
    difference: function (p1, p2) {
      try {
        return window.polygonClipping.difference(p1, p2);
      }
      catch (error) {
        console.debug(error);
        console.log('caught error while trying to calculate polygon difference. using first input as return value');
        return p1;
      }
    },
    /**
     *
     * @param {[number,number][]} p1
     * @param {[number,number][]} p2
     * @returns {[number, number][]}
     */
    intersection: function (p1, p2) {
      try {
        return window.polygonClipping.intersection(p1, p2);
      }
      catch (error) {
        console.debug(error);
        console.log('caught error while trying to calculate polygon intersection. using first input as return value');
        return p1;
      }
    },
    /**
     *
     * @param {[number,number][]} p1
     * @param {[number,number][]} p2
     * @returns {[number, number][]}
     */
    union: function (p1, p2) {
      try {
        return window.polygonClipping.union(p1, p2);
      }
      catch (error) {
        console.debug(error);
        console.log('caught error while trying to calculate polygon union. using first input as return value');
        return p1;
      }
    },
    /**
     *
     * @param {[number,number][]} p1
     * @param {[number,number][]} p2
     * @returns {[number, number][]}
     */
    xor: function (p1, p2) {
      try {
        return window.polygonClipping.xor(p1, p2);
      }
      catch (error) {
        console.debug(error);
        console.log('caught error while trying to calculate polygon xor. using first input as return value');
        return p1;
      }
    },
  },
  /**
   *
   * @param {Point} v1
   * @param {Point} v2
   */
  angleOfVectors(v1, v2) {
    return uMath.vectorAngle(v1, v2);
    /* var dotP = uMath.dotProduct(v1, v2);
    var lengthProd = uMath.vectorLength(v1) * uMath.vectorLength(v2);
    var arccos = Math.acos(shiftRound(dotP / lengthProd));
    return uMath.radToDeg(arccos); */
  },
  /**
   *
   * @param {Segment} seg1
   * @param {Segment} seg2
   * @param {number} threshold
   * @returns {boolean}
   */
  areSegmentsPerpendicular(seg1, seg2, threshold = 1) {
    var v1 = uMath.vectorFromSegment(seg1);
    var v2 = uMath.vectorFromSegment(seg2);
    var angle = uMath.normalizeAngle(uMath.angleOfVectors(v1, v2));
    return angle >= 90 - threshold / 2 && angle <= 90 + threshold / 2 || angle >= 270 - threshold / 2 && angle <= 270 + threshold / 2;
  },
  /**
   *
   * @param {Segment} seg1
   * @param {Segment} seg2
   * @param {number} threshold
   * @returns {boolean}
   */
  areSegmentsParallel(seg1, seg2, threshold = 1) {
    var v1 = uMath.vectorFromSegment(seg1);
    var v2 = uMath.vectorFromSegment(seg2);
    var angle = uMath.normalizeAngle(uMath.angleOfVectors(v1, v2));
    return angle >= 0 - threshold / 2 && angle <= 0 + threshold / 2 || angle >= 180 - threshold / 2 && angle <= 180 + threshold / 2;
  },
  areSegmentsSimilarLong(seg1, seg2, threshold = 2) {
    var len1 = uMath.segmentLength(seg1);
    var len2 = uMath.segmentLength(seg2);
    if (Math.abs(len1 - len2) <= threshold) {
      return { result: true, length: (len1 + len2) / 2 };
    }
    else {
      return { result: false, length: 0 };
    }
  },
  /**
   *
   * @param {room} room
   * @returns {boolean}
   */
  roomAllWallsNarrow(room) {
    try {
      if (!room) { return false; }
      const walls = wallsOfRoom(room);
      return walls.every(wall => {
        const tryPcnt = [];
        for (let i = 0.1; i <= 0.9; i += 0.1) { tryPcnt.push(i); }
        const wallPt1 = segPt(wall, 1);
        const wallPt2 = segPt(wall, 2);
        return tryPcnt.every(tryPcnt => {
          const pt = uMath.lerpPt(wallPt1, wallPt2, tryPcnt);
          const snapPt = snapDeviceToWall(pt, PlannerGlobals.DEVICE_SIZE * 2, undefined, undefined, undefined, undefined, undefined, true);
          return !snapPt;
        });
      });
    }
    catch (err) {
      console.error(err);
      return false;
    }
  },
  /**
   * @param {Point} start
   * @param {Point} end
   * @returns {Point}
   */
  getSnappedEnd: function (start, end) {
    if (uMath.pointDistance(start, end) < 1) {
      return end;
    }
    var deg = vectorRotationDegree(start, end);
    var snapped = snapAngleTo45_2(deg);
    var newEnd = uMath.pointAtAngleDistance(start, snapped, uMath.pointDistance(start, end));
    var closestPt = lineClosestPointToPoint(end, start, newEnd);
    return closestPt;
  },
  array: {
    /**
     *
     * @param {number[]} array
     */
    sum: function (array) {
      return array.reduce(function (a, b) { return a + b }, 0);
    },
    /**
     *
     * @param {number[]} array
     */
    sumFromTo: function (array, from, to) {
      return uMath.array.sum(array.slice(from, to));
    },
    /**
     *
     * @param {number[]} array
     */
    mean: function (array) {
      if (!array.length) {
        return 0;
      }
      return uMath.array.sum(array) / array.length;
    },
    /**
     *
     * @param {number[]} array
     */
    stdDev: function (array) {
      var avg = uMath.array.mean(array);
      var squareDiffs = array.map(function (value) {
        var diff = value - avg;
        var sqrDiff = diff * diff;
        return sqrDiff;
      });
      var avgSquareDiff = uMath.array.mean(squareDiffs);
      var stdDev = Math.sqrt(avgSquareDiff);
      return stdDev;
    },
    /**
     *
     * @param {any[]} array
     * @returns {any}
     */
    last: function (array) {
      return array[array.length - 1];
    }
  },

  /**
   * Returns nth point of a segment
   * @param {Segment} segment
   * @param {number} n
   * @returns {Point}
   * @example nThSegmentPoint({x1: 1, y1: 1, x2: 2, y2: 2}, 1) => {x: 1, y: 1}
   */
  nThSegmentPoint(segment, n) {
    return segPt(segment, n);
  },
  normalizeInRange: function (num, min, max) {
    return (num - min) / (max - min);
  },
  segmentsOverlap: function (s1, s2) {
    for (var i = 1; i <= 2; i++) {
      var pt1 = segPt(s1, i);
      var pt2 = segPt(s2, i);
      if (isPointOnSegment(pt1, s2) || isPointOnSegment(pt2, s1)) {
        return true;
      }
    }
    return false;
  },
  /**
   *
   * @param {Rect} rect1
   * @param {Rect} rect2
   * @returns
   */
  rectsOverlap: function (rect1, rect2) {
    return !(rect1.x + rect1.width < rect2.x ||
        rect1.x > rect2.x + rect2.width ||
        rect1.y + rect1.height < rect2.y ||
        rect1.y > rect2.y + rect2.height);
  },
  /**
   *
   * @param {DOMRect} rect1
   * @param {DOMRect} rect2
   * @returns
   */
  DOMRectsOverlap: function (rect1, rect2) {
    return !(rect1.right < rect2.left ||
        rect1.left > rect2.right ||
        rect1.bottom < rect2.top ||
        rect1.top > rect2.bottom);
  },
  /**
   *
   * @param {DOMRect} rect1
   * @param {DOMRect} rect2
   * @returns
   */
  DOMRectOverlapArea: function (r1, r2) {
    const left = Math.max(r1.left, r2.left);
    const right = Math.min(r1.right, r2.right);
    const bottom = Math.min(r1.bottom, r2.bottom);
    const top = Math.max(r1.top, r2.top);
    const width = right - left;
    const height = bottom - top;
    return width * height;
  },
  /**
   *
   * @param {Point[]} pts
   * @param {Segment} segment
   * @param {number} threshold
   */
  isAnyPointOnSegment: function (pts, segment, threshold = 0.05) {
    return pts.some(pt => isPointOnSegment(pt, segment, threshold));
  },
  /**
   *
   * @param {Point} pt
   * @param {Segment[]} segments
   * @param {number} threshold
   * @returns
   */
  isPointOnAnySegment: function (pt, segments, threshold = 0.05) {
    return segments.some(seg => isPointOnSegment(pt, seg, threshold));
  },
  /**
   *
   * @param {Point} pt
   * @param {Segment[]} segments
   * @param {number} threshold
   * @returns
   */
  findSegmentPointIsOn: function (pt, segments, threshold = 0.05) {
    return segments.find(seg => isPointOnSegment(pt, seg, threshold));
  },
  crossProductLength: function (A, B, C) {
    var BAx = A.x - B.x;
    var BAy = A.y - B.y;
    var BCx = C.x - B.x;
    var BCy = C.y - B.y;

    return (BAx * BCy - BAy * BCx);
  },
  /**
   *
   * @param {Point[]} polygon
   */
  isPolygonConvex: function (polygon) {
    var got_negative = false;
    var got_positive = false;

    var B, C;
    for (var A = 0; A < polygon.length; A++) {
      B = (A + 1) % polygon.length;
      C = (B + 1) % polygon.length;

      var cross_product = uMath.crossProductLength(polygon[A], polygon[B], polygon[C]);
      if (cross_product < 0) {
        got_negative = true;
      }
      else if (cross_product > 0) {
        got_positive = true;
      }
      if (got_negative && got_positive) {
        return false;
      }
    }
    return true;
  },
  floatsSimilar(f1, f2, threshold = 0.001) {
    return Math.abs(f1 - f2) <= threshold;
  },
  /**
   *
   * @param {Point} P
   * @param {Point} A
   * @param {Point} B
   */
  pointSegmentDistance: function (P, A, B) {
    var closestPt = segmentClosestPointToPoint(P, A, B);
    return uMath.pointDistance(P, closestPt);
  },
  /**
   *
   * @param {Point} P
   * @param {Segment} S
   */
  pointSegmentDistance_S: function (P, S) {
    var closestPt = segmentClosestPointToPoint_S(P, S);
    return uMath.pointDistance(P, closestPt);
  },
  /**
   *
   * @param {Point} point
   * @param {Point[]} poly
   */
  pointPolyDistance: function (point, poly) {
    const segDists = uMath.segmentsOfPolygon(poly)
    .map(seg => uMath.pointSegmentDistance_S(point, seg))
    .sort((a, b) => a - b);

    if (segDists.length) {
      return segDists[0];
    }
    else {
      return -1;
    }

  },
  getRndInteger: function (min, max) {
    return Math.floor(Math.random() * (max - min)) + min;
  },
  /**
   *
   * @param {NPoly} poly
   * @param {NPoint[]} points
   */
  polyContainsEveryPoint: function (poly, points) {
    return points.every(function (pt) {
      return d3.polygonContains(poly, pt);
    });
  },
  /**
   *
   * @param {NPoly} poly
   * @param {NPoint[]} points
   */
  polyContainsAtLeastOnePoint: function (poly, points) {
    return points.some(function (pt) {
      return d3.polygonContains(poly, pt);
    });
  },

  polyDoesntContainAnyPoint: function (poly, points) {
    return !uMath.polyContainsAtLeastOnePoint(poly, points);
  },
  /**
   * Adds points
   * @param {Point} point1
   * @param {Point} point2
   */
  addPoints: function (point1, point2) {
    return { x: point1.x + point2.x, y: point1.y + point2.y };
  },
  /**
   * Subtracts point2 from point1
   * @param {Point} point1
   * @param {Point} point2
   */
  subtractPoints: function (point1, point2) {
    return { x: point1.x - point2.x, y: point1.y - point2.y };
  },
  /**
   * Multiplies point with scalar
   * @param {number} scalar
   * @param {Point} point
   */
  multiplyScalarPoint: function (scalar, point) {
    return { x: point.x * scalar, y: point.y * scalar };
  },
  /**
   *
   * @param {number} x
   * @param {number} y
   * @returns
   */
  createPoint: function (x, y) {
    return { x: x, y: y };
  },
  /**
   *
   * @param {Segment} s
   * @param {Point} p
   * @returns
   */
  getProjectedPointOnSegment: function (s, p) {
    const v1 = { x: s.x1, y: s.y1 };
    const v2 = { x: s.x2, y: s.y2 };
    // get dot product of e1, e2
    const e1 = this.createPoint(v2.x - v1.x, v2.y - v1.y);
    const e2 = this.createPoint(p.x - v1.x, p.y - v1.y);
    const valDp = this.dotProduct(e1, e2);
    // get length of vectors
    let lenLineE1 = Math.sqrt(e1.x * e1.x + e1.y * e1.y);
    let lenLineE2 = Math.sqrt(e2.x * e2.x + e2.y * e2.y);
    if (lenLineE1 === 0) { lenLineE1 = 0.000001 }
    if (lenLineE2 === 0) { lenLineE2 = 0.000001 }
    const cos = valDp / (lenLineE1 * lenLineE2);
    // length of v1P'
    const projLenOfLine = cos * lenLineE2;
    const retP = this.createPoint((v1.x + (projLenOfLine * e1.x) / lenLineE1),
        (v1.y + (projLenOfLine * e1.y) / lenLineE1));
    return retP;
  },
  /**
   *
   * @param {Segment} s
   * @param {Point} p
   * @returns
   */
  getProjectedPointOnSegmentFast: function (s, p) {
    const v1 = { x: s.x1, y: s.y1 };
    const v2 = { x: s.x2, y: s.y2 };
    // get dot product of e1, e2
    const e1 = this.createPoint(v2.x - v1.x, v2.y - v1.y);
    const e2 = this.createPoint(p.x - v1.x, p.y - v1.y);
    const valDp = this.dotProduct(e1, e2);
    // get squared length of e1
    const len2 = e1.x * e1.x + e1.y * e1.y;
    const retP = this.createPoint((v1.x + (valDp * e1.x) / len2),
        (v1.y + (valDp * e1.y) / len2));
    return retP;
  },
  wallsOfRoom(room) {
    var walls = [];

    for (var currPt = 0; currPt < room.poly.length; currPt++) {
      walls.push({
        x1: room.poly[currPt].x,
        y1: room.poly[currPt].y,
        x2: room.poly[(currPt + 1) % room.poly.length].x,
        y2: room.poly[(currPt + 1) % room.poly.length].y,
        id: room.poly[currPt].id,
        roomId: room.id
      });
    }

    return walls;
  },
  /**
   *
   * @param {Room} room
   * @param {Point} point
   * @returns
   */
  isPointOnRoomCorner(room, point, threshold = 1) {
    if (!room) { console.debug('no room supplied'); return false; }
    return room.poly.find(roomPt => uMath.pointDistance(point, roomPt) < threshold);
  }
};

/**
 *
 * @param {Point[]} polygon
 * @returns {[number, number][]}
 */
export function XYtoNumPoly(polygon) {
  return polygon.map(function (XY) { return [XY.x, XY.y]; });
}

/**
 *
 * @param {[number, number][]} polygon
 * @returns {Point[]}
 */
export function NumToXYPoly(polygon) {
  return polygon.map(function (numarr) { return { x: numarr[0], y: numarr[1] } });
}

/**
 *
 * @param {any[]} arr
 */
export function _does(arr) {
  return {
    /**
     *
     * @param {any} item
     */
    contain: function (item) {
      return arr.indexOf(item) != -1;
    }
  }
}

/**
 *
 * @param {Line} l1
 * @param {Line} l2
 */
export function infiniteLineIntersection(l1, l2) {
  var line1StartX = l1.x1,
      line1StartY = l1.y1,
      line1EndX = l1.x2,
      line1EndY = l1.y2,
      line2StartX = l2.x1,
      line2StartY = l2.y1,
      line2EndX = l2.x2,
      line2EndY = l2.y2;

  // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
  var denominator, a, b, numerator1, numerator2, result = {
    x: null,
    y: null,
    onLine1: false,
    onLine2: false
  };
  denominator = ((line2EndY - line2StartY) * (line1EndX - line1StartX)) - ((line2EndX - line2StartX) * (line1EndY - line1StartY));
  if (denominator == 0) {
    return result;
  }
  a = line1StartY - line2StartY;
  b = line1StartX - line2StartX;
  numerator1 = ((line2EndX - line2StartX) * a) - ((line2EndY - line2StartY) * b);
  numerator2 = ((line1EndX - line1StartX) * a) - ((line1EndY - line1StartY) * b);
  a = numerator1 / denominator;
  b = numerator2 / denominator;

  // if we cast these lines infinitely in both directions, they intersect here:
  result.x = line1StartX + (a * (line1EndX - line1StartX));
  result.y = line1StartY + (a * (line1EndY - line1StartY));
  /*
          // it is worth noting that this should be the same as:
          x = line2StartX + (b * (line2EndX - line2StartX));
          y = line2StartX + (b * (line2EndY - line2StartY));
          */
  // if line1 is a segment and line2 is infinite, they intersect if:
  if (a > 0 && a < 1) {
    result.onLine1 = true;
  }
  // if line2 is a segment and line1 is infinite, they intersect if:
  if (b > 0 && b < 1) {
    result.onLine2 = true;
  }
  // if line1 and line2 are segments, they intersect if both of the above are true
  return result;
}

export function shiftRound(num) {
  return (num + (num > 0 ? 0.5 : -0.5)) << 0;
}

export function snapAngleTo45(inAngle, threshold) {
  var angle = inAngle;
  angle = inAngle % 360;
  if (angle < 0) {
    angle = Math.abs(360 + angle);
  }
  var times45 = Math.floor(angle / 45);
  var rem45 = angle % 45;
  if (rem45 <= threshold) {
    return times45 * 45;
  }
  else if (45 - rem45 <= threshold) {
    return (times45 + 1) * 45;
  }
  return inAngle;
}

export function snapAngleTo45_2(angle) {
  var currAngle = 360;
  while (currAngle >= 0) {
    if (angle > currAngle - 45 / 2) {
      return currAngle;
    }
    currAngle -= 45;
  }
}

/**
 *
 * @param {Point | Device} pointOrDev
 * @param {number} snapTolerance
 * @param {number} length
 * @param {boolean} addCenter true: result will be the serial's center. false: result will be serial's first dev
 * @param {boolean} noSafeArea
 * @param {number} maxIter
 * @param {boolean} justClosestWall
 * @param {boolean} avoidNarrowWalls prevent snapping if there is a close wall
 * @param {boolean} serialRows number of rows in vertical serial / serial connection
 * @returns {{point: Point | Device, rotation: number, room: Room, wall: Wall}}
 */
export function snapDeviceToWall(pointOrDev, snapTolerance, length = 1, addCenter = false,
    noSafeArea = false, maxIter = 30, justClosestWall = false, avoidNarrowWalls = false,
    serialRows = 1) {
  if (!UDevGlobals.WALLS_CACHE || !Array.isArray(UDevGlobals.WALLS_CACHE)) {
    return false;
  }

  var wallId = pointOrDev.wallId; // not a required property but can be useful when sorting the walls

  let innerRoomWalls = [];
  if (avoidNarrowWalls) {
    const roomSource = (typeof PlannerGlobals.allRooms !== "undefined" && Array.isArray(PlannerGlobals.allRooms)) ? PlannerGlobals.allRooms : uDev.roomsExtended;
    innerRoomWalls = roomSource.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder)).map(room => wallsOfRoom(room)).reduce((p, c) => p.concat(c), []);
  }

  if (UDevGlobals.WALLS_CACHE.length == 0) {
    if (PlannerGlobals.rooms.length > 0) {
      console.debug("Wall cache is empty!");
    }
    return false;
  }
  var width = length * PlannerGlobals.DEVICE_SIZE + (length - 1) * SERIAL_SPACING;
  var height = PlannerGlobals.DEVICE_SIZE * serialRows + SERIAL_SPACING * (serialRows - 1);
  var isDevCamera = isCamera(pointOrDev);
  var pointOrDev = { x: pointOrDev.x, y: pointOrDev.y };
  if (addCenter) {
    var pointOrDev = { x: pointOrDev.x + width / 2, y: pointOrDev.y + height / 2 };
  }

  for (var i = 0; i < UDevGlobals.WALLS_CACHE.length; i++) {
    UDevGlobals.WALLS_CACHE[i].snapDist = distToWall(pointOrDev, UDevGlobals.WALLS_CACHE[i]);
  }

  UDevGlobals.WALLS_CACHE.sort(function (w1, w2) {
    if (wallId !== undefined) {
      // try to keep the wall if the device is already snapping to any
      if (wallId === w1.id && w1.snapDist < snapTolerance) {
        return -1;
      } else if (wallId === w2.id && w2.snapDist < snapTolerance) {
        return 1;
      }
    }
    return w1.snapDist < w2.snapDist ? -1 : 1;
  });

  var MAX_ITER = maxIter;
  var DIST_ITER = 0.5;
  var MAX_WALL_ITER = justClosestWall ? 1 : 5;
  var wallStartPt, wallEndPt;
  var safeArea = noSafeArea ? 1 : 1.18;

  for (var i = 0; i < MAX_WALL_ITER && i < UDevGlobals.WALLS_CACHE.length; i++) {
    var currWall = UDevGlobals.WALLS_CACHE[i];
    var snapPt = wallClosestPointToPoint(pointOrDev, currWall);
    if (uMath.pointDistance(pointOrDev, snapPt) < snapTolerance) {
      var pt1 = { x: currWall.x1, y: currWall.y1 };
      var pt2 = { x: currWall.x2, y: currWall.y2 };
      if (uMath.pointDistance(snapPt, pt1) < uMath.pointDistance(snapPt, pt2)) {
        wallStartPt = pt1;
        wallEndPt = pt2;
      }
      else {
        wallStartPt = pt2;
        wallEndPt = pt1;
      }

      var snapTryRot = vectorRotationDegree(wallStartPt, wallEndPt);
      var wallRot = wallRotation(currWall);
      if (currWall.haveToInvertRot) {
        wallRot = (wallRot + 180) % 360;
      }
      var room = getRoomById(currWall.roomId);

      if (!room) {
        return false;
      }

      if (avoidNarrowWalls) {
        const parallelAndCloseWall = innerRoomWalls.find(otherWall => {
          if (otherWall === currWall || otherWall.id === currWall.id) { return false; }
          const isParallel = uMath.areSegmentsParallel(currWall, otherWall, 2);
          if (!isParallel) { return false; }
          const tryPts = [snapPt];
          tryPts.push(uMath.pointAtAngleDistance(snapPt, wallRot, width / 2));
          tryPts.push(uMath.pointAtAngleDistance(snapPt, wallRot, -width / 2));

          for (let i = 0; i < tryPts.length; i++) {
            const pt = tryPts[i];
            const closestPt = wallClosestPointToPoint(pt, otherWall);
            const isClose = uMath.pointDistance(closestPt, pt) < CMToPx(8);
            if (isClose) { return true; }
          }

          return false;
          /* const closestPt = wallClosestPointToPoint(snapPt, otherWall);
          const isClose = uMath.pointDistance(closestPt, snapPt) < CMToPx(8);
          return isClose; */
        });
        if (parallelAndCloseWall) { continue; }
      }

      snapPt.x = snapPt.x - width / 2 - uMath.sinD(wallRot) * (height / 2 + safeArea);
      snapPt.y = snapPt.y - height / 2 + uMath.cosD(wallRot) * (height / 2 + safeArea);

      var currIter = 0;
      while (currIter < MAX_ITER) {
        for(var j = 1; j >= -1; j -= 2) { // i in [1, -1] - try to move moddedPt in both direction
          var moddedPt = uMath.pointAtAngleDistance(snapPt, snapTryRot, currIter * j * DIST_ITER);
          if (doesDeviceFitRoom(room.id, moddedPt, wallRot, length, serialRows)) {
            if (isDevCamera) {
              wallRot += 90;
            }
            return { point: moddedPt, rotation: wallRot, room: room, wall: currWall }
          }
        };
        currIter++;
      }
    }
  }
  return false;
}


export function sqr(x) {
  return x * x;
}

export function dist2(v, w) {
  return sqr(v.x - w.x) + sqr(v.y - w.y);
}

/**
 *
 * @param {Point} p Point
 * @param {Point} v Segment start
 * @param {Point} w Segment end
 */
export function distToSegmentSquared(p, v, w) {
  var l2 = dist2(v, w);

  if (l2 == 0) return dist2(p, v);

  var t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;

  if (t < 0) return dist2(p, v);
  if (t > 1) return dist2(p, w);

  return dist2(p, { x: v.x + t * (w.x - v.x), y: v.y + t * (w.y - v.y) });
}

/**
 *
 * @param {Point} p
 * @param {Line} wall
 */
export function distToWall(p, wall) {
  return distToSegment(p, { x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
}

/**
 *
 * @param {Point} p point
 * @param {Point} v lineStart
 * @param {Point} w lineEnd
 */
export function distToSegment(p, v, w) {
  return Math.sqrt(distToSegmentSquared(p, v, w));
}

export function segPt(s, i) {
  return { x: s['x' + i], y: s['y' + i] };
}

/**
 * is point on line / is point on segment / onsegment / online
 * @param {Point} p
 * @param {Segment} s
 */
export function isPointOnSegment(p, s, threshold = 0.05) {
  return distToSegment(p, segPt(s, 1), segPt(s, 2)) < threshold;
}


/**
 *
 * @param {Point} P
 * @param {Line} wall
 * @returns {Point}
 */
export function wallClosestPointToPoint(P, wall) {
  return segmentClosestPointToPoint(P, { x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
}

/**
 * Closest point from point to line, defined by two points
 * @param {*} P
 * @param {*} A
 * @param {*} B
 */
export function lineClosestPointToPoint(P, A, B) {
  var a_to_p = [P.x - A.x, P.y - A.y];
  var a_to_b = [B.x - A.x, B.y - A.y];

  var atb2 = Math.pow(a_to_b[0], 2) + Math.pow(a_to_b[1], 2);
  if (atb2 == 0) {
    return B;
  }
  var atp_dot_atb = a_to_p[0] * a_to_b[0] + a_to_p[1] * a_to_b[1];

  var t = atp_dot_atb / atb2;

  return {
    x: A.x + a_to_b[0] * t,
    y: A.y + a_to_b[1] * t
  };
}

export function infiniteLinePointDistance(P, A, B) {
  return uMath.lineLength(uMath.segmentFromPoints(P, lineClosestPointToPoint(P, A, B)));
}

/**
 *
 * @param {Point} P
 * @param {Segment} S
 */
export function segmentClosestPointToPoint_S(P, S) {
  return segmentClosestPointToPoint(P, { x: S.x1, y: S.y1 }, { x: S.x2, y: S.y2 });
}

/**
 * Closest point from point to SEGMENT, defined by two points
 * @param {Point} P
 * @param {Point} A
 * @param {Point} B
 * @returns {Point}
 */
export function segmentClosestPointToPoint(P, A, B) {
  var origin = A;
  var end = B;
  var point = P;
  var heading = { x: end.x - origin.x, y: end.y - origin.y };
  var magnitudeMax = uMath.vectorLength(heading);
  uMath.normalize(heading, 1);

  var lhs = { x: point.x - origin.x, y: point.y - origin.y };
  var dotP = uMath.dotProduct(lhs, heading);
  dotP = uMath.clamp(dotP, 0, magnitudeMax);
  return { x: origin.x + heading.x * dotP, y: origin.y + heading.y * dotP };
}
 export let C180PERPI = 180 / Math.PI;

/**
 * Return the rotation of a vector compared to a horizontal line. COUNTER clockwise is POSITIVE.
 * @param {Point} A
 * @param {Point} B
 * @returns {number}
 */
export function vectorRotationDegree(A, B) {
  // horizontal
  var a = B.x - A.x;
  // vertical
  var b = B.y - A.y;

  var deg = Math.atan(b / a) * C180PERPI;
  // down right
  if (a >= 0 && b >= 0) {
    // nothing
  }
  // down left
  else if (a < 0 && b >= 0) {
    deg = 180 + deg;
  }
  // up left
  else if (a < 0 && b < 0) {
    deg = 180 + deg;
  }
  // up right
  else if (a >= 0 && b < 0) {
    deg = 360 + deg;
  }
  if (Number.isNaN(deg)) {
    deg = 0;
  }
  return deg;
}

export function wallRotation(wall) {
  var rot = vectorRotationDegree({ x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
  var room = getRoomById(wall.roomId);
  if (room && !uMath.isRoomClockwise(room)) {
    rot -= 180;
  }
  return rot;

}

/**
 *
 * @param {NPoint[]} devPts
 * @param {NPoint[]} poly
 */
export function devAndPolyIntersects(devPts, poly) {
  return uMath.polygonsIntersect(NumToXYPoly(devPts), NumToXYPoly(poly));
}

/**
 *
 * @param {[number,number][]} roomPoly
 * @param {Point} devUpperLeftPoint
 * @param {number} devRotation
 * @param {number} serialLength
 * @param {number} serialRows
 */
export function doesDeviceFitRoom(roomId, devUpperLeftPoint, devRotation, serialLength = 1, serialRows = 1) {
  var room = getRoomById(roomId) || getModRoomIfId(roomId);
  if (!room) { return false; }

  if (roomId == uDev.OUTDOORS_ROOM_ID) {
    // outer wall is marked
    if (uDev.outerBorderExists()) {

      const outerBorderPoly = XYto2DArr(PlannerGlobals.mode == "modifyOuterW" ? PlannerGlobals.modifiedPoly : PlannerGlobals.outerBorder);

      var devPts = getDeviceRealPoints(devUpperLeftPoint, devRotation, serialLength, serialRows);
      return doesDeviceFitPoly(XYto2DArr(room.poly), devUpperLeftPoint, devRotation, serialLength, serialRows) &&
          !devPts.some(function (devPt) {
            return d3.polygonContains(outerBorderPoly, devPt) ||
                uDev.getOutdoorRoomPolies().some(function (poly) {
                  return d3.polygonContains(poly, devPt);
                });
          }) &&
          !devAndPolyIntersects(devPts, outerBorderPoly) &&
          !getOuterRooms(PlannerGlobals.rooms, PlannerGlobals.outerBorder)
          .some(function (room) { return devAndPolyIntersects(devPts, XYto2DArr(room.poly)); });

    }
    // outer wall isn't marked
    else {
      return false;
      /*  return doesDeviceFitPoly(XYto2DArr(room.poly), devUpperLeftPoint, devRotation, serialLength); */
    }

  }
  else if (roomId == uDev.UNASSIGNED_ROOM_ID) {
    const devPts = getDeviceRealPoints(devUpperLeftPoint, devRotation, serialLength, serialRows);

    // device still has to be inside the plan eg 0,0 -> 900,900 rect
    const uplanRect = [[0, 0], [0, 900], [900, 900], [900, 0]];
    const inUplanRect = devPts.every(pt => d3.polygonContains(uplanRect, pt));
    if (!inUplanRect) { return false; }

    const outerBorder2DArr = XYto2DArr(PlannerGlobals.outerBorder);
    const devInsideAnyRoom = PlannerGlobals.rooms.some(room => d3.polygonContains(XYto2DArr(room.poly), devPts[0]));
    const anyRoomIntersect = PlannerGlobals.rooms.some(room => devAndPolyIntersects(devPts, XYto2DArr(room.poly)));
    const outerBorderIntersect = uDev.outerBorderExists() && devAndPolyIntersects(devPts, outerBorder2DArr);
    const outerBorderExistsAndOutside = uDev.outerBorderExists() && devPts.some(pt => !d3.polygonContains(outerBorder2DArr, pt));

    return !devInsideAnyRoom && !anyRoomIntersect && !outerBorderIntersect && !outerBorderExistsAndOutside;
  }
  else {
    var roomPoly = XYto2DArr(room.poly);
    var devPts = getDeviceRealPoints(devUpperLeftPoint, devRotation, serialLength, serialRows);
    return doesDeviceFitPoly(roomPoly, devUpperLeftPoint, devRotation, serialLength, serialRows) &&
        !devAndPolyIntersects(devPts, roomPoly);
  }
}

/**
 *
 * @param {Point} devUpperLeftPoint
 * @param {number} devRotation
 * @param {number} serialLength
 * @param {number} serialRows
 */
export function getDeviceRealPoints(devUpperLeftPoint, devRotation, serialLength = 1, serialRows = 1) {
  var pts = [];
  var width = PlannerGlobals.DEVICE_SIZE * serialLength + (serialLength - 1) * SERIAL_SPACING;
  var height = PlannerGlobals.DEVICE_SIZE * serialRows + SERIAL_SPACING * (serialRows - 1);
  var mods = [
    [0, 0],
    [0, 1],
    [1, 1],
    [1, 0]
  ]
  for (var i = 0; i < mods.length; i++) {
    pts.push(rotatePointAroundAnother(
        [devUpperLeftPoint.x + width * mods[i][0], devUpperLeftPoint.y + height * mods[i][1]],
        [devUpperLeftPoint.x + width / 2, devUpperLeftPoint.y + height / 2],
        devRotation));

  }
  return pts;
}

/**
 *
 * @param {[number,number][]} roomPoly
 * @param {Point} devUpperLeftPoint
 * @param {number} devRotation
 * @param {number} serialLength
 * @param {number} serialRows
 */
export function doesDeviceFitPoly(roomPoly, devUpperLeftPoint, devRotation, serialLength = 1, serialRows = 1) {
  if (!roomPoly) {
    return false;
  }
  var width = PlannerGlobals.DEVICE_SIZE * serialLength + (serialLength - 1) * SERIAL_SPACING;
  var height = PlannerGlobals.DEVICE_SIZE * serialRows + SERIAL_SPACING * (serialRows - 1);
  for (var i = 0; i <= 1; i++) {
    for (var j = 0; j <= 1; j++) {
      var rotated = rotatePointAroundAnother(
          [devUpperLeftPoint.x + width * i, devUpperLeftPoint.y + height * j],
          [devUpperLeftPoint.x + width / 2, devUpperLeftPoint.y + height / 2],
          devRotation);
      if (!uMath.polygonContains(roomPoly, rotated)) {
        return false;
      }
    }
  }
  return true;
}

export function getClosestWallToPoint(point) {
  var minVal = distToWall(point, UDevGlobals.WALLS_CACHE[0]);
  var minIdx = 0;
  for (var i = 1; i < UDevGlobals.WALLS_CACHE.length; i++) {
    var dist = distToWall(point, UDevGlobals.WALLS_CACHE[i]);
    if (dist < minVal) {
      minVal = dist;
      minIdx = i;
    }
  }

  return UDevGlobals.WALLS_CACHE[minIdx];
}

/**
 *
 * @param {Segment[]} segments
 * @param {Point} point
 */
export function getClosestSegmentToPoint(segments, point) {
  var minVal = distToWall(point, segments[0]);
  var minIdx = 0;
  for (var i = 1; i < segments.length; i++) {
    var dist = distToWall(point, segments[i]);
    if (dist < minVal) {
      minVal = dist;
      minIdx = i;
    }
  }

  return segments[minIdx];
}

/**
 *
 * @param {Point} toRotate
 * @param {Point} otherPoint
 * @param {number} angle
 * @returns {Point}
 */
export function rotatePointAroundAnotherXY(toRotate, otherPoint, angle) {
  return P_numtoxy(rotatePointAroundAnother(P_xytonum(toRotate), P_xytonum(otherPoint), angle));
}

/**
 *
 * @param {[number, number]} toRotate
 * @param {[number, number]} otherPoint
 * @param {number} angle
 * @returns {[number, number]}
 */
export function rotatePointAroundAnother(toRotate, otherPoint, angle) {
  angle = degToRad(angle);
  var cos = Math.cos;
  var sin = Math.sin;

  var newPoint = [];

  newPoint[0] = cos(angle) * (toRotate[0] - otherPoint[0]) - sin(angle) * (toRotate[1] - otherPoint[1]) + otherPoint[0];
  newPoint[1] = sin(angle) * (toRotate[0] - otherPoint[0]) + cos(angle) * (toRotate[1] - otherPoint[1]) + otherPoint[1];

  return newPoint;
}

export function radToDeg(rad) {
  return rad * 180 / Math.PI;
}

export function degToRad(angle) {
  return angle * Math.PI / 180;
}

//helper method for line_line_intersect

export function btwn(a, b1, b2) {
  var rounder = 10000000;
  a = Math.round(a * rounder) / rounder;
  b1 = Math.round(b1 * rounder) / rounder;
  b2 = Math.round(b2 * rounder) / rounder;

  if ((a >= b1) && (a <= b2)) { return true; }
  if ((a >= b2) && (a <= b1)) { return true; }
  return false;
}


/**
 * Returns an intersection point if the two given lines intersect. Otherwise, returns 'nir'
 *
 * @param {Line} line1
 * @param {Line} line2
 * @returns
 */
export function line_line_intersect(line1, line2) {
  var x1 = line1.x1, x2 = line1.x2, x3 = line2.x1, x4 = line2.x2;
  var y1 = line1.y1, y2 = line1.y2, y3 = line2.y1, y4 = line2.y2;
  var pt_denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  var pt_x_num = (x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4);
  var pt_y_num = (x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4);
  if (Math.abs(pt_denom) <= 0.00000001) { return "nir"; }
  else {
    var pt = { 'x': pt_x_num / pt_denom, 'y': pt_y_num / pt_denom };
    if (btwn(pt.x, x1, x2) && btwn(pt.y, y1, y2) && btwn(pt.x, x3, x4) && btwn(pt.y, y3, y4)) { return pt; }
    else { return "nir"; }
  }
}

/**
 * Returns an intersection point if the two given lines intersect. Otherwise, returns 'nir'
 *
 * @param {Line} line1
 * @param {Line} line2
 * @returns
 */
export function line_line_intersect2(line1, line2) {
  var x1 = line1.x1, x2 = line1.x2, x3 = line2.x1, x4 = line2.x2;
  var y1 = line1.y1, y2 = line1.y2, y3 = line2.y1, y4 = line2.y2;

  var pt_denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(pt_denom) <= 0.00000001) { return false; }
  else {
    var pt_x_num = (x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4);
    var pt_y_num = (x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4);
    var pt = {
      'x': pt_x_num / pt_denom,
      'y': pt_y_num / pt_denom
    };
    if (btwn(pt.x, x1, x2) && btwn(pt.y, y1, y2) && btwn(pt.x, x3, x4) && btwn(pt.y, y3, y4)) { return pt; }
    else { return false; }
  }
}

export function ftToCm(ft) {
  return ft * 30.48;
}

export function cmToFt(cm) {
  return cm / 30.48;
}

export function CMToPx(CM) {
  return CM / PlannerGlobals.LENGTHOF1PX;
}
export function P_numtoxy(arr) {
  return { x: arr[0], y: arr[1] };
}

export function P_numtoxyPlusHalf(arr) {
  return { x: arr[0] + PlannerGlobals.DEVICE_SIZE / 2, y: arr[1] + PlannerGlobals.DEVICE_SIZE / 2 };
}

export function P_xytonum(xy) {
  return [xy.x, xy.y];
}

export function P_xytonumPlusHalf(xy) {
  return [xy.x + PlannerGlobals.DEVICE_SIZE / 2, xy.y + PlannerGlobals.DEVICE_SIZE / 2];
}

/**
 * @param {Point} p
 */
export function PointPlusHalf(p) {
  return { x: p.x + PlannerGlobals.DEVICE_SIZE / 2, y: p.y + PlannerGlobals.DEVICE_SIZE / 2 };
}

/**
 * @param {Point[]} poly
 * @returns {[number, number][]}
 */
export function XYto2DArr(poly) {
  return poly.map(function (XY) {
    return [XY.x, XY.y]
  });
}

/**
 *
 * @param {Room} room
 */
export function getRoomAreaCM2(room) {
  var poly = XYtoNumPoly(room.poly);
  var area = Math.abs(d3.polygonArea(poly));
  var proparea = area * Math.pow(PlannerGlobals.LENGTHOF1PX, 2);
  return proparea;
}

/**
 *
 * @param {Room} room
 */
export function getRoomVolumeCM3(room) {
  return getRoomAreaCM2(room) * room.room_headroom;
}

export function getRoomVolumeM3(room) {
  return getRoomVolumeCM3(room) / 1000000;
}

/**
 *
 * @param {Room} room
 * @returns {Wall[]}
 */
export function wallsOfRoom(room) {
  var walls = [];

  for (var currPt = 0; currPt < room.poly.length; currPt++) {
    var c = room.poly[currPt];
    var n = room.poly[(currPt + 1) % room.poly.length];
    walls.push({
      x1: c.x,
      y1: c.y,
      x2: n.x,
      y2: n.y,
      roomId: room.id,
      id: c.id,
      order_index: c.order_index
    });
  }

  return walls;
}

/**
 *
 * @param {Wall} wall
 * @param {number} granularity
 * @returns {Point[]}
 */
export function pointsOfWall(wall, granularity = 0.5, posOffset = 0) {
  var len = uMath.lineLength(wall);
  var rot = uMath.lineRotationDegree(wall);
  var begin = { x: wall.x1, y: wall.y1 };
  var pts = [];
  for (var pos = posOffset; pos < len - posOffset; pos += granularity) {
    var pt = uMath.pointAtAngleDistance(begin, rot, pos);
    pt.wallId = wall.id;
    pts.push(pt);
  }
  return pts;
}

/**
 *
 * @param {Room} room
 * @param {number} granularity
 * @returns {Point[]}
 */
export function pointsOfWalls(walls, granularity = 0.5, posOffset = 0) {
  var pts = [];
  walls.forEach(function (wall) {
    pts = pts.concat(pointsOfWall(wall, granularity, posOffset));
  });
  return pts;
}


/**
 *
 * @param {Point[]} fromPoints
 * @param {Wall[]} walls
 * @param {number} granularity
 * @returns {Point[]}
 */
export function furthestPointsOnWalls(fromPoints, walls, granularity, preferredWall) {
  var wallsPoints = pointsOfWalls(walls, granularity, 5);
  var distances = wallsPoints.map(function (wallPoint) {
    var pointDistances = fromPoints.map(function (sourcePoint) {
      return uMath.pointDistance(wallPoint, sourcePoint);
    });
    var distSum = uMath.array.sum(pointDistances);
    return {
      wallPoint,
      distSum
    }
  });
  distances.sort(function (a, b) {
    return a.distSum > b.distSum ? -1 : 1;
  });
  var preferredDistances = [], notPreferredDistances = [];
  if (preferredWall) {
    for (var i = 0; i < distances.length; i++) {
      if (distances[i].wallPoint.wallId === preferredWall.id) {
        preferredDistances.push(distances[i]);
      }
      else {
        notPreferredDistances.push(distances[i]);
      }
    }
  }
  else {
    preferredDistances = distances;
  }


  return preferredDistances.concat(notPreferredDistances).map(function (pt) { return pt.wallPoint; });
}

/**
 *
 * @param {Point[]} fromPoints
 * @param {Wall[]} walls
 * @param {number} granularity
 * @returns {Point}
 */
export function furthestPointOnWalls(fromPoints, walls, granularity) {
  var distances = furthestPointsOnWalls(fromPoints, walls, granularity);
  return distances[0].wallPoint;
}

/**
 *
 * @param {Room} room
 * @param {number} order_index
 * @returns {Wall}
 */
export function getWallByRoomAndIndex(room, order_index) {
  var curr = room.poly.find(function (pt) { return pt.order_index == order_index });
  var next = room.poly.find(function (pt) { return pt.order_index == (order_index + 1) % room.poly.length });
  return { x1: curr.x, y1: curr.y, x2: next.x, y2: next.y, id: curr.id, roomId: room.id, order_index: order_index };
}

/**
 *
 * @param {number} id
 * @returns {Wall | false}
 */
export function getWallById(id) {
  for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
    var walls = wallsOfRoom(PlannerGlobals.rooms[i]);
    for (var j = 0; j < walls.length; j++) {
      if (walls[j].id == id) {
        return walls[j];
      }
    }
  }
  return false;
}

/**
 *
 * @param {string | number} id
 * @returns {Wall | false}
 */
export function getWallOrBorderWallById(id) {
  id = "" + id;
  if (uDev.outerBorderExists() && id.indexOf('OW_') === 0) {
    id = id.replace('OW_', '');
    const walls = wallsOfRoom({ poly: PlannerGlobals.outerBorder });
    return walls.find(wall => wall.id == id);
  }
  else {
    for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
      var walls = wallsOfRoom(PlannerGlobals.rooms[i]);
      for (var j = 0; j < walls.length; j++) {
        if (walls[j].id == id) {
          return walls[j];
        }
      }
    }
  }
  return false;
}

/**
 *
 * @param {any[]} arr1
 * @param {any[]} arr2
 * @returns {any[]}
 */
export function arrayIntersection(arr1, arr2) {
  return arr1.filter(function (value) { return arr2.indexOf(value) != -1 });
}

export function countIndexes(from, to, limit, down = false) {
  if (from == to) {
    return [];
  }
  var indexes = [];
  var curr = from;
  while (curr != to) {
    indexes.push(curr);
    if (down) {
      curr--;
      if (curr < 0) {
        curr = limit - 1;
      }
    }
    else {
      curr++;
      curr = curr % limit;
    }
  }

  return indexes;
}

export function countIndexesIncl(from, to, limit, down = false) {
  if (from < 0 || to < 0 || limit <= 0 || from >= limit || to >= limit) {
    return [];
  }
  if (from == to) {
    return [from];
  }
  var indexes = [];
  var curr = from;
  while (curr != to) {
    indexes.push(curr);
    if (down) {
      curr--;
      if (curr < 0) {
        curr = limit - 1;
      }
    }
    else {
      curr++;
      curr = curr % limit;
    }
  };
  indexes.push(curr);

  return indexes;
}


export function createSVGtext(caption, x, y) {
  //  This function attempts to create a new svg "text" element, chopping
  //  it up into "tspan" pieces, if the caption is too long
  //
  var svgText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  svgText.setAttributeNS(null, 'x', x);
  svgText.setAttributeNS(null, 'y', y);
  svgText.setAttributeNS(null, 'font-size', 12);
  svgText.setAttributeNS(null, 'fill', '#FFFFFF');         //  White text
  svgText.setAttributeNS(null, 'text-anchor', 'middle');   //  Center the text

  //  The following two variables should really be passed as parameters
  var MAXIMUM_CHARS_PER_LINE = 20;
  var LINE_HEIGHT = 16;

  var words = caption.split(" ");
  var line = "";

  for (var n = 0; n < words.length; n++) {
    var testLine = line + words[n] + " ";
    if (testLine.length > MAXIMUM_CHARS_PER_LINE) {
      //  Add a new <tspan> element
      var svgTSpan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      svgTSpan.setAttributeNS(null, 'x', x);
      svgTSpan.setAttributeNS(null, 'y', y);

      var tSpanTextNode = document.createTextNode(line);
      svgTSpan.appendChild(tSpanTextNode);
      svgText.appendChild(svgTSpan);

      line = words[n] + " ";
      y += LINE_HEIGHT;
    }
    else {
      line = testLine;
    }
  }

  var svgTSpan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
  svgTSpan.setAttributeNS(null, 'x', x);
  svgTSpan.setAttributeNS(null, 'y', y);

  var tSpanTextNode = document.createTextNode(line);
  svgTSpan.appendChild(tSpanTextNode);

  svgText.appendChild(svgTSpan);

  return svgText;
}

/**
 * @deprecated [DEPRECATED] Use SVG_TEXT_HELPER instead
 * @param {d3.Selection} text
 * @param {*} width
 */
export function wrapMultilineText(text, width) {
  text.each(function () {
    var text = d3.select(this),
        words = text.text().split(/\s+/).reverse(),
        word,
        line = [],
        lineNumber = 0,
        lineHeight = 1.1, // ems
        x = text.attr("x"),
        y = text.attr("y"),
        dy = 0;

    var textStr = text.text();
    var isSymbol = textStr.indexOf("_") != -1;
    if (isSymbol) {
      var lastUnderscoreIdx = textStr.lastIndexOf("_");
      var firstPart = textStr.slice(0, lastUnderscoreIdx);
      var numPart = textStr.slice(lastUnderscoreIdx + 1, textStr.length);

      text.text(null);
      text.append("tspan")
        .attr('x', x)
        .attr('y', y)
        .attr('dy', 0)
        .text(firstPart);
      text.append("tspan")
        .attr('x', x)
        .attr('y', y)
        .attr('dy', 1.1 + "em")
        .text(numPart);
    }
    else {
      var tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

      while (word = words.pop()) {
        line.push(word);
        tspan.text(line.join(" ").replace(/\ ?\_\ ?/, '_'));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];
          tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
        }
      }
    }
  });
}

export function roundTo(num, base) {
  return base * Math.round(num / base);
}

function isNum(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export function isPointValid(p) {
  return !!p && p.x && p.y && isNum(p.x) && isNum(p.y);
}

export function makeSVG(tag) {
  return document.createElementNS('http://www.w3.org/2000/svg', tag);
}

export function setXlinkHref(elem, value) {
  elem.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', value);
}

/**
 *
 * @param {Node} svg
 * @param {function} callback
 */
export function svgToRasterImageNode(svg, width, height, renderScale, callback) {
  if (typeof callback === "function") {
    try {
      var svgstring = "data:image/svg+xml;base64," + btoa(new XMLSerializer().serializeToString(svg));

      var svgwrapper = new Image(width * renderScale, height * renderScale);
      svgwrapper.width = width * renderScale;
      svgwrapper.height = height * renderScale;
      svgwrapper.src = svgstring;

      svgwrapper.onload = function () {
        var svgcanvas = document.createElement("canvas");
        svgcanvas.width = width * renderScale;
        svgcanvas.height = height * renderScale;

        var svgctx = svgcanvas.getContext("2d");
        svgctx.drawImage(svgwrapper, 0, 0, width * renderScale, height * renderScale);

        var b64 = svgcanvas.toDataURL('image/png');

        var image = makeSVG('image');
        setXlinkHref(image, b64);
        image.setAttribute('x', '0');
        image.setAttribute('y', '0');
        image.setAttribute('width', width);
        image.setAttribute('height', height);

        return callback(image);
      }

      svgwrapper.onerror = function (err) {
        console.error(err);
        return callback(false);
      }
    }
    catch (err) {
      console.error(err);
      return callback(false);
    }
  }
  else {
    throw "callback in svgToImg should be a function";
  }
}
